import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };
  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next) return toast.error("Fill both fields");
    if (next.length < 4) return toast.error("Password must be at least 4 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      close();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent data-testid="change-password-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
            Change password
          </DialogTitle>
          <DialogDescription>Use a strong password — at least 8 characters recommended.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <PasswordField
            label="Current password"
            value={current}
            onChange={setCurrent}
            testid="cp-current-input"
            autoFocus
          />
          <PasswordField
            label="New password"
            value={next}
            onChange={setNext}
            testid="cp-new-input"
          />
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            testid="cp-confirm-input"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={close}
              className="rounded-full border-[#e5e5eb]"
            >
              Cancel
            </Button>
            <Button
              data-testid="cp-submit-btn"
              type="submit"
              disabled={submitting}
              className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
            >
              {submitting ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordField({ label, value, onChange, testid, autoFocus }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-2 block">
        {label}
      </label>
      <div className="relative">
        <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a65]" />
        <Input
          data-testid={testid}
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          className="h-12 pl-11 rounded-xl border-[#e5e5eb] font-mono-data"
          required
        />
      </div>
    </div>
  );
}
